import { db } from "../firebaseClient"
import {
  collection, collectionGroup, doc, addDoc, setDoc, updateDoc, getDoc, getDocs,
  onSnapshot, query, where, orderBy, serverTimestamp, increment,
} from "firebase/firestore"
import { getNesolearnId } from "./nesolearnIdHelpers"
import { logPlatformEvent } from "./eventHelpers"

const mentorsCol = (orgId) => collection(db, "organizations", orgId, "mentors")
const mentorDoc = (orgId, uid) => doc(db, "organizations", orgId, "mentors", uid)
const mentorshipsCol = (orgId) => collection(db, "organizations", orgId, "mentorships")
const mentorshipDoc = (orgId, id) => doc(db, "organizations", orgId, "mentorships", id)

// ── Designating a mentor ──
// Gated on the candidate already having a Nesolearn ID — no one guides
// another learner's journey before establishing their own verified identity.
export async function designateMentor(orgId, { uid, name, email, courseId }) {
  const { nesolearnId } = await getNesolearnId(uid)
  if (!nesolearnId) {
    return { error: { message: "This person needs to claim a Nesolearn ID before they can be designated as a mentor." } }
  }

    await setDoc(mentorDoc(orgId, uid), {
    uid,
    name: name || "",
    email: email || "",
    nesolearnId,
    courseId: courseId || null, // the course they're qualified to mentor in, if scoped to one
    verified: true,             // org-assigned mentors are org-verified by default
    verifiedAt: Date.now(),
    verificationSource: "org",  // "org" | "platform" — platform-wide self-verification is a later phase
    status: "active",
    createdAt: serverTimestamp(),
  })
  logPlatformEvent({
    type: "MENTOR_DESIGNATED",
    actorUid: uid,
    orgId,
    targetPath: `organizations/${orgId}/mentors/${uid}`,
    metadata: { name: name || "", courseId: courseId || null },
  })
  return { error: null }
}

export async function removeMentor(orgId, uid) {
  await updateDoc(mentorDoc(orgId, uid), { status: "removed" })
  logPlatformEvent({
    type: "MENTOR_REMOVED",
    orgId,
    targetPath: `organizations/${orgId}/mentors/${uid}`,
    severity: "warning",
    metadata: {},
  })
}

export function subscribeMentors(orgId, cb) {
  return onSnapshot(mentorsCol(orgId), (snap) => {
    cb(snap.docs.map((d) => d.data()).filter((m) => m?.status !== "removed"))
  })
}

export async function getMentorEligibility(uid) {
  const { nesolearnId } = await getNesolearnId(uid)
  return { eligible: !!nesolearnId, nesolearnId }
}

// ── Pairing a mentor with a mentee ──

export async function createMentorship(orgId, {
  mentorUid, mentorName, menteeUid, menteeName, courseId, cohortId, goal,
}) {
  const ref = await addDoc(mentorshipsCol(orgId), {
    mentorUid, mentorName: mentorName || "",
    menteeUid, menteeName: menteeName || "",
    courseId: courseId || null,
    cohortId: cohortId || null,
    goal: goal || "",
    status: "active",
    createdAt: serverTimestamp(),
    endedAt: null,
  })
  logPlatformEvent({
    type: "MENTORSHIP_CREATED",
    orgId,
    targetPath: `organizations/${orgId}/mentorships/${ref.id}`,
    metadata: { mentorUid, menteeUid, courseId: courseId || null, cohortId: cohortId || null },
  })
  return { mentorshipId: ref.id }
}

export async function endMentorship(orgId, mentorshipId) {
  await updateDoc(mentorshipDoc(orgId, mentorshipId), { status: "ended", endedAt: Date.now() })
  logPlatformEvent({
    type: "MENTORSHIP_ENDED",
    orgId,
    targetPath: `organizations/${orgId}/mentorships/${mentorshipId}`,
    metadata: {},
  })
}

// For the org's Mentors panel — every pairing in this org.
export function subscribeMentorships(orgId, cb) {
  const q = query(mentorshipsCol(orgId), orderBy("createdAt", "desc"))
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
}

// For a mentor's own workspace — just their mentees, across whichever
// org(s) they mentor in. Kept single-org for now (matches how the rest of
// the platform is org-scoped) — cross-org "My Mentees" comes with Phase B.
export function subscribeMyMentees(orgId, mentorUid, cb) {
  const q = query(mentorshipsCol(orgId), where("mentorUid", "==", mentorUid), where("status", "==", "active"))
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
}

// ── Sessions (logged 1:1 check-ins) ──

export async function logMentorshipSession(orgId, mentorshipId, { notes, completedAtMs }) {
  await addDoc(collection(db, "organizations", orgId, "mentorships", mentorshipId, "sessions"), {
    notes: notes || "",
    completedAtMs: completedAtMs || Date.now(),
    createdAt: serverTimestamp(),
  })
}

export function subscribeMentorshipSessions(orgId, mentorshipId, cb) {
  const q = query(
    collection(db, "organizations", orgId, "mentorships", mentorshipId, "sessions"),
    orderBy("completedAtMs", "desc")
  )
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
}

// ── Private mentor notes on a mentee ──

export async function addMentorshipNote(orgId, mentorshipId, text) {
  await addDoc(collection(db, "organizations", orgId, "mentorships", mentorshipId, "notes"), {
    text: text || "",
    createdAt: serverTimestamp(),
  })
}

export function subscribeMentorshipNotes(orgId, mentorshipId, cb) {
  const q = query(
    collection(db, "organizations", orgId, "mentorships", mentorshipId, "notes"),
    orderBy("createdAt", "desc")
  )
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
}

// ── Cross-org mentee lookup, for the mentor's own workspace ──
// A mentor doc lives at organizations/{orgId}/mentorships/{id}, so a
// collectionGroup query is the only way to find every mentorship this
// person mentors across ALL orgs, not just one. orgId isn't stored as a
// field on the mentorship doc itself — it's derived from the doc's path
// (doc.ref.parent.parent.id) since that's always organizations/{orgId}/mentorships/{id}.
// NOTE: Firestore will likely prompt you to create a composite index the
// first time this runs — click the link in the console error, it's a
// one-time setup, not a bug.
export function subscribeAllMyMentees(mentorUid, cb) {
  const q = query(
    collectionGroup(db, "mentorships"),
    where("mentorUid", "==", mentorUid),
    where("status", "==", "active")
  )
  return onSnapshot(q, (snap) => {
    cb(snap.docs.map((d) => ({ id: d.id, orgId: d.ref.parent.parent.id, ...d.data() })))
  })
}

// ── Cross-org "am I a mentor anywhere" check, for the dashboard entry point ──
// Same collectionGroup pattern as subscribeAllMyMentees, but queries the
// "mentors" collection group instead of "mentorships" — this tells us
// whether the signed-in user has been designated a mentor in ANY org, even
// if they haven't been paired with a mentee yet.
export function subscribeIsMentorAnywhere(uid, cb) {
  const q = query(
    collectionGroup(db, "mentors"),
    where("uid", "==", uid),
    where("status", "==", "active")
  )
  return onSnapshot(q, (snap) => {
    cb(!snap.empty)
  }, (error) => {
    console.error("subscribeIsMentorAnywhere failed:", error)
  })
}

// Mirrors subscribeAllMyMentees, but from the mentee's side — every active
// mentorship where this user is the one being mentored, across all orgs.
export function subscribeMyMentors(menteeUid, cb) {
  const q = query(
    collectionGroup(db, "mentorships"),
    where("menteeUid", "==", menteeUid),
    where("status", "==", "active")
  )
  return onSnapshot(q, (snap) => {
    cb(snap.docs.map((d) => ({ id: d.id, orgId: d.ref.parent.parent.id, ...d.data() })))
  })
}

// ── Messages (threaded chat scoped to one mentorship pairing) ──
// Nested directly under the mentorship doc rather than a separate
// conversationId scheme — the mentorship itself IS the unique
// conversation key, since a pairing is 1:1 by construction.

const mentorshipDocRef = (orgId, mentorshipId) => doc(db, "organizations", orgId, "mentorships", mentorshipId)

export async function sendMentorshipMessage(orgId, mentorshipId, { uid, name, text }) {
  const trimmed = (text || "").trim()
  if (!trimmed) return

  await addDoc(collection(db, "organizations", orgId, "mentorships", mentorshipId, "messages"), {
    uid,
    name: name || "",
    text: trimmed,
    createdAt: serverTimestamp(),
  })

  // Bump the other party's unread count and leave a short preview on the
  // pairing doc itself, so a future inbox/list view can show "last message"
  // without reading every message subcollection.
  const snap = await getDoc(mentorshipDocRef(orgId, mentorshipId))
  const data = snap.data() || {}
  const otherUid = data.mentorUid === uid ? data.menteeUid : data.mentorUid
  if (!otherUid) return

  await setDoc(mentorshipDocRef(orgId, mentorshipId), {
    lastMessage: trimmed.slice(0, 140),
    lastMessageAt: serverTimestamp(),
    [`unread_${otherUid}`]: increment(1),
  }, { merge: true })
}

export function subscribeMentorshipMessages(orgId, mentorshipId, cb) {
  const q = query(
    collection(db, "organizations", orgId, "mentorships", mentorshipId, "messages"),
    orderBy("createdAt", "asc")
  )
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
}

// Marks this user caught up — clears their unread count and stamps when
// they last read, so the other party's read-receipt tick can compare
// against it.
export async function markMentorshipRead(orgId, mentorshipId, uid) {
  await setDoc(mentorshipDocRef(orgId, mentorshipId), {
    [`lastRead_${uid}`]: serverTimestamp(),
    [`unread_${uid}`]: 0,
  }, { merge: true })
}

export async function setMentorshipTyping(orgId, mentorshipId, uid, isTyping) {
  await setDoc(mentorshipDocRef(orgId, mentorshipId), {
    [`typing_${uid}`]: isTyping,
  }, { merge: true })
}

// Live view of the mentorship doc's chat-related fields (read receipts,
// typing, last message) — kept separate from the sessions/notes reads so
// the chat UI can subscribe to just this without re-fetching the whole doc.
export function subscribeMentorshipMeta(orgId, mentorshipId, cb) {
  return onSnapshot(mentorshipDocRef(orgId, mentorshipId), (snap) => cb(snap.data() || null))
}

// ── Editing / deleting a sent message — sender only, soft delete so the
// thread keeps its shape ("This message was deleted") rather than leaving
// a gap. ──

export async function editMentorshipMessage(orgId, mentorshipId, messageId, text) {
  const trimmed = (text || "").trim()
  if (!trimmed) return
  await updateDoc(doc(db, "organizations", orgId, "mentorships", mentorshipId, "messages", messageId), {
    text: trimmed,
    editedAt: serverTimestamp(),
  })
}

export async function deleteMentorshipMessage(orgId, mentorshipId, messageId) {
  await updateDoc(doc(db, "organizations", orgId, "mentorships", mentorshipId, "messages", messageId), {
    text: "",
    deleted: true,
  })
}

// ── Appointments (schedule-ahead sessions) — separate from the
// after-the-fact session log. One party proposes a time, the other
// confirms or declines; a confirmed appointment shows as "Upcoming" on
// both dashboards. ──

export async function proposeAppointment(orgId, mentorshipId, { proposedByUid, scheduledForMs, note }) {
  await addDoc(collection(db, "organizations", orgId, "mentorships", mentorshipId, "appointments"), {
    proposedByUid,
    scheduledForMs,
    note: note || "",
    status: "proposed",
    createdAt: serverTimestamp(),
    respondedAt: null,
    respondedBy: null,
  })
}

export async function respondToAppointment(orgId, mentorshipId, appointmentId, { uid, status }) {
  await updateDoc(doc(db, "organizations", orgId, "mentorships", mentorshipId, "appointments", appointmentId), {
    status, // "confirmed" | "declined"
    respondedAt: serverTimestamp(),
    respondedBy: uid,
  })
}

export async function cancelAppointment(orgId, mentorshipId, appointmentId, uid) {
  await updateDoc(doc(db, "organizations", orgId, "mentorships", mentorshipId, "appointments", appointmentId), {
    status: "cancelled",
    respondedAt: serverTimestamp(),
    respondedBy: uid,
  })
}

export function subscribeAppointments(orgId, mentorshipId, cb) {
  const q = query(
    collection(db, "organizations", orgId, "mentorships", mentorshipId, "appointments"),
    orderBy("scheduledForMs", "asc")
  )
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
}

// One-time (non-realtime) read of a confirmed appointment scheduled for
// today, for one mentorship — the dashboard's Today's Focus widget checks
// this per-mentorship on load rather than keeping a live listener per pairing.
export async function getTodaysConfirmedAppointment(orgId, mentorshipId) {
  const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0)
  const endOfDay = new Date(); endOfDay.setHours(23, 59, 59, 999)

  const q = query(
    collection(db, "organizations", orgId, "mentorships", mentorshipId, "appointments"),
    where("status", "==", "confirmed")
  )
  const snap = await getDocs(q)
  return (
    snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .find((a) => a.scheduledForMs >= startOfDay.getTime() && a.scheduledForMs <= endOfDay.getTime()) || null
  )
}